import { AppliedPayment, StockIndication } from '@spa/generated/graphql/graphql';
import {
  Cart,
  CartDiscountCodes,
  CartDiscountOnEntryPrice,
  CartDiscountOnEntryPricePerQuantity,
  CartDiscountOnTotalPrice,
  CartEntry
} from '../graphql/cart/findOneCart';
import {
  EnergyEfficiencyDatasheet,
  ProductAssetsFields,
  ProductDatasheet,
  ProductInfoProductAssetType
} from '@spa/components/ProductAssetTypes';
import {
  StateCart,
  StateCartAppliedPayment,
  StateCartAppliedPaymentGiftCard,
  StateCartDeliveryAddress,
  StateCartDiscountCode,
  StateCartDiscountOnEntryPricePerQuantity,
  StateCartDiscountOnPrice,
  StateCartDiscountOnTotalPrice,
  StateCartDiscountedAmount,
  StateCartEntry,
  StateCartPayment,
  StateCartPaymentAddress
} from '../store/carts/CartsState';
import convertAddDiscountCodeStatus from './convertAddDiscountCodeStatus';
import convertIfNotUndefinedOrNull from './convertIfNotUndefinedOrNull';
import { convertProductAssetsToProductFields } from '@spa/pages-converters/products/convertProductAssetsToProductFields';
import safelyConvertEnum from './safelyConvertEnum';

const convertCartPayment = ({
  descriptor,
  interfaceId,
  mode
}: Exclude<Cart['payment'], null>): StateCartPayment => ({
  descriptor,
  interfaceId,
  mode: safelyConvertEnum(mode)
});

const convertAppliedPaymentAmount = (
  source: AppliedPayment['amountPlanned']
): StateCartDiscountedAmount | null => {
  return source
    ? {
        valueWithTax: source.valueWithTax,
        currencyIso: safelyConvertEnum(source.currencyIso)
      }
    : null;
};

const convertAppliedPaymentGiftCard = (
  source: AppliedPayment['giftCard']
): StateCartAppliedPaymentGiftCard | null => {
  return source
    ? {
        balance: convertAppliedPaymentAmount(source.balance),
        onlineId: source.onlineId,
        remainingBalance: convertAppliedPaymentAmount(source.remainingBalance),
        transactionId: source.transactionId
      }
    : null;
};

export const convertAppliedPaymentToStateCartAppliedPayment = ({
  amountPlanned,
  code,
  giftCard,
  id,
  method,
  paymentInterface
}: AppliedPayment): StateCartAppliedPayment => ({
  amountPlanned: convertAppliedPaymentAmount(amountPlanned),
  code: safelyConvertEnum(code),
  giftCard: convertAppliedPaymentGiftCard(giftCard),
  id,
  method,
  paymentInterface
});

export const convertCartEntriesToStateCartEntries = (
  source: Array<CartEntry>
): ReadonlyArray<StateCartEntry> =>
  source.map((entry: CartEntry): StateCartEntry => {
    const { entryMode, product, productReference, priceMode }: CartEntry = entry;
    const {
      energyEfficiencyLabel,
      energyEfficiencyOverview,
      productDataSheets
    }: ProductAssetsFields = convertProductAssetsToProductFields(product.assets);

    const energyDataSheet: EnergyEfficiencyDatasheet | undefined = productDataSheets?.find(
      (dataSheet: ProductDatasheet): dataSheet is EnergyEfficiencyDatasheet =>
        dataSheet.type === ProductInfoProductAssetType.ENERGY_EFFICIENCY_DATASHEET
    );

    const {
      badges = null,
      categories = [],
      categoryWithParents = [],
      ownBrand = null,
      stockLevel = 0,
      stockLevelIndication = StockIndication.OUT_OF_STOCK
    } = productReference.product ?? {};

    return {
      ...entry,
      entryMode: safelyConvertEnum(entryMode),
      product: {
        ...product,
        badges,
        categories,
        categoryWithParents,
        energyEfficiencyLabel,
        energyEfficiencyOverview,
        energyEfficiencyDataSheet: energyDataSheet ?? null,
        ownBrand,
        stockLevel,
        stockLevelIndication,
        averageRating: null,
        reviewCount: null
      },
      priceMode: safelyConvertEnum(priceMode),
      discountOnCartEntryPricePerQuantity:
        convertDiscountsToStateCartDiscountOnEntryPricePerQuantity(
          entry.discountOnCartEntryPricePerQuantity
        ),
      tracking: entry.tracking
        ? {
            itemListId: entry.tracking.itemListId ?? undefined,
            itemListName: entry.tracking.itemListName ?? undefined
          }
        : undefined
    };
  });

const convertCartDeliveryAddressToStatePaymentAddress = ({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  __typename: _,
  ...address
}: Exclude<Cart['deliveryAddress'], null>): StateCartDeliveryAddress => {
  return {
    ...address,
    additionalAddressInfo: address.additionalAddressInfo
  };
};

const convertCartPaymentAddressToStatePaymentAddress = ({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  __typename: _,
  ...address
}: Exclude<Cart['paymentAddress'], null>): StateCartPaymentAddress => {
  return {
    ...address,
    additionalAddressInfo: address.additionalAddressInfo
  };
};

export const convertDiscountsToStateCartDiscountOnTotalPrice = (
  source: CartDiscountOnTotalPrice | null
): StateCartDiscountOnTotalPrice | null => {
  return source
    ? {
        totalDiscount: source.totalDiscount,
        includedDiscounts: source.includedDiscounts.map((discount) => ({
          ...discount,
          type: safelyConvertEnum(discount.type),
          absoluteValue: discount.absoluteValue
            ? {
                valueWithTax: discount.absoluteValue.valueWithTax,
                currencyIso: safelyConvertEnum(discount.absoluteValue.currencyIso)
              }
            : null
        }))
      }
    : null;
};

const convertDiscountsToStateCartDiscountOnEntryPrice = (
  source: CartDiscountOnEntryPrice | null
): StateCartDiscountOnPrice | null => {
  return source
    ? {
        totalDiscount: source.totalDiscount,
        includedDiscounts: source.includedDiscounts.map((discount) => ({
          ...discount,
          type: safelyConvertEnum(discount.type),
          absoluteValue: discount.absoluteValue
            ? {
                valueWithTax: discount.absoluteValue.valueWithTax,
                currencyIso: safelyConvertEnum(discount.absoluteValue.currencyIso)
              }
            : null
        }))
      }
    : null;
};

export const convertDiscountsToStateCartDiscountOnEntryPricePerQuantity = (
  source: CartDiscountOnEntryPricePerQuantity | null
): ReadonlyArray<StateCartDiscountOnEntryPricePerQuantity> | null =>
  source?.map(({ quantity, discountedPrice }) => ({
    quantity: quantity,
    discountedPrice: convertDiscountsToStateCartDiscountOnEntryPrice(discountedPrice)
  })) ?? null;

// Order responses share this converter but do not request cart state or benefit information.
type ConvertibleDiscountCode = Omit<
  NonNullable<CartDiscountCodes>[number],
  'hasBenefit' | 'state'
> & {
  readonly state?: NonNullable<CartDiscountCodes>[number]['state'];
  readonly hasBenefit?: boolean | null;
};

export const convertDiscountCodesToStateCartDiscountCodes = (
  source: ReadonlyArray<ConvertibleDiscountCode> | null
): ReadonlyArray<StateCartDiscountCode> => {
  return source
    ? source.map((cartDiscountCode) => {
        const {
          id,
          code,
          discounts,
          valid,
          state,
          hasBenefit,
          hasFullShippingCostsDiscount,
          validUntil,
          isEmployeeDiscount
        } = cartDiscountCode;

        return {
          id,
          code,
          discounts: discounts?.length ? discounts : [],
          valid,
          state: state == null ? state : convertAddDiscountCodeStatus(state),
          hasBenefit,
          validUntil: validUntil ?? null,
          hasFullShippingCostsDiscount: hasFullShippingCostsDiscount ?? false,
          isEmployeeDiscount
        };
      })
    : [];
};

const convertCartToStateCart = (source: Cart): StateCart => {
  return {
    ...source,
    deliveryAddress: convertIfNotUndefinedOrNull(
      source.deliveryAddress,
      convertCartDeliveryAddressToStatePaymentAddress
    ),
    paymentAddress: convertIfNotUndefinedOrNull(
      source.paymentAddress,
      convertCartPaymentAddressToStatePaymentAddress
    ),
    entries: convertCartEntriesToStateCartEntries(source.entries),
    payment: convertIfNotUndefinedOrNull(source.payment, convertCartPayment),
    payments: source.payments?.map(convertAppliedPaymentToStateCartAppliedPayment) ?? [],
    discountCodes: convertDiscountCodesToStateCartDiscountCodes(source.discountCodes),
    discountOnTotalPrice: convertDiscountsToStateCartDiscountOnTotalPrice(
      source.discountOnTotalPrice
    )
  };
};

export default convertCartToStateCart;
